/**
 * Utility functions for detecting duplicate entries
 */

/**
 * Check if an array has duplicates based on ID field
 * @param items Array to check
 * @param arrayName Name of the array (for error message)
 * @returns Object with hasDuplicates flag and count
 */
export function checkForDuplicates<T extends { id: string }>(
  items: T[],
  arrayName: string
): { hasDuplicates: boolean; duplicateCount: number } {
  if (!items || items.length === 0) {
    return { hasDuplicates: false, duplicateCount: 0 }
  }

  const ids = new Set<string>()
  let duplicateCount = 0

  for (const item of items) {
    if (item && item.id) {
      if (ids.has(item.id)) {
        duplicateCount++
      } else {
        ids.add(item.id)
      }
    }
  }

  if (duplicateCount > 0) {
    console.error(
      `⚠️ DUPLICATES DETECTED: ${arrayName} has ${duplicateCount} duplicate entries. Please use the Check/Clean button in Settings to fix this.`
    )
  }

  return { hasDuplicates: duplicateCount > 0, duplicateCount }
}

/**
 * Check multiple arrays for duplicates and log all issues
 */
export function checkAllForDuplicates(checks: Array<{ items: any[]; name: string }>) {
  let totalDuplicates = 0
  const issues: string[] = []

  for (const { items, name } of checks) {
    const result = checkForDuplicates(items, name)
    if (result.hasDuplicates) {
      totalDuplicates += result.duplicateCount
      issues.push(`${name}: ${result.duplicateCount}`)
    }
  }

  if (totalDuplicates > 0) {
    console.error(
      `⚠️ TOTAL DUPLICATES: ${totalDuplicates} across ${issues.length} arrays\n` +
      `Details: ${issues.join(', ')}\n` +
      `Fix: Go to Settings → Check/Clean button`
    )
  }

  return { totalDuplicates, issues }
}
